import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { BancaViewModel } from '../../viewmodel/banca/BancaViewModel';
import { EnviarEmailHeteroidentificacao } from '../../viewmodel/ComunicacaoCandidato/EmailHeteroidentificacao';

interface AcoesSecretarioModalProps {
  isOpen: boolean;
  onClose: () => void;
  bancaId: string;
  onBancaFinalizada?: () => void;
  banca?: any;
}

const AcoesSecretarioModal: React.FC<AcoesSecretarioModalProps> = ({ 
  isOpen, 
  onClose, 
  bancaId, 
  onBancaFinalizada,
  banca
}) => {
  const [counter, setCounter] = useState<number>(6);
  const [isSubmittingFinalizar, setIsSubmittingFinalizar] = useState(false);
  const [isSubmittingAta, setIsSubmittingAta] = useState(false);
  const [ocorrencias, setOcorrencias] = useState<string[]>([]);
  const [novaOcorrencia, setNovaOcorrencia] = useState<string>('');
  const [bancaFinalizada, setBancaFinalizada] = useState(banca?.status === 'FINALIZADA');

  useEffect(() => {
    if (isOpen) {
      setCounter(6);
      setBancaFinalizada(banca?.status === 'FINALIZADA');
      const timer = setInterval(() => {
        setCounter((prevCounter) => prevCounter - 1);
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [isOpen, banca?.status]);

  useEffect(() => {
    if (counter === 0) {
      setIsSubmittingFinalizar(false);
      setIsSubmittingAta(false);
    }
  }, [counter]);

  const handleAddOcorrencia = (event: React.MouseEvent | React.KeyboardEvent) => {
    event.stopPropagation();
    if (novaOcorrencia.trim() === '') {
      toast.error('A ocorrência não pode estar vazia.');
      return;
    }

    setOcorrencias([...ocorrencias, novaOcorrencia]);
    setNovaOcorrencia('');
  };

  const handleRemoveOcorrencia = (index: number) => {
    setOcorrencias(ocorrencias.filter((_, i) => i !== index));
  };

  const handleFinalizar = async (event: React.MouseEvent) => {
    event.stopPropagation();
    setIsSubmittingFinalizar(true);
    try {
      const ocorrenciaTexto = ocorrencias.length === 0 ? '(sem ocorrências)' : ocorrencias.join(', ');
      await BancaViewModel.finalizarBancaSemAta(bancaId, ocorrenciaTexto);
      toast.success('Banca finalizada com sucesso!');

      setBancaFinalizada(true);
      onBancaFinalizada?.();
    } catch (error: any) {
      const errorMessage = error.response?.data[0]?.message || 'Erro ao finalizar banca';
      toast.error(`${errorMessage}`);
    } finally {
      setIsSubmittingFinalizar(false);
    }
  };

  const handleCriarAta = async (event: React.MouseEvent) => {
    event.stopPropagation();
    setIsSubmittingAta(true);
    try {
      await BancaViewModel.criarAta(bancaId);
      toast.success('Ata criada com sucesso!');
      onBancaFinalizada?.();
    } catch (error: any) {
      const errorMessage = error.response?.data[0]?.message || 'Erro ao criar ata';
      toast.error(`${errorMessage}`);
    } finally {
      setIsSubmittingAta(false);
    }
  };

  const handleClose = () => {
    setIsSubmittingFinalizar(false);
    setIsSubmittingAta(false);
    setCounter(0);
    setOcorrencias([]);
    setNovaOcorrencia('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div 
      className="modal fade show" 
      style={{ 
        display: 'block', 
        backgroundColor: 'rgba(0,0,0,0.5)',
        zIndex: 1050
      }} 
      tabIndex={-1}
      onClick={handleClose}
    >
      <div className="modal-dialog modal-lg" onClick={(e) => e.stopPropagation()}>
        <div className="modal-content">
          <div className="modal-header bg-primary text-white">
            <h5 className="modal-title d-flex align-items-center">
              <i className="bi bi-clipboard-check me-2"></i>
              Ações do Secretário
            </h5>
            <button type="button" className="btn-close btn-close-white" onClick={handleClose}></button>
          </div>
          <div className="modal-body p-4">
            <div className="alert alert-info mb-4">
              <strong>Secretário:</strong> Escolha a ação que deseja realizar para esta banca.
            </div>

            {ocorrencias.length > 0 && (
              <div className="card mb-4">
                <div className="card-header bg-light">
                  <h6 className="mb-0">Ocorrências Registradas</h6>
                </div>
                <div className="card-body" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                  {ocorrencias.map((ocorrencia, index) => (
                    <div key={index} className="d-flex justify-content-between align-items-center py-2 border-bottom">
                      <span className="flex-grow-1">{ocorrencia}</span>
                      <button 
                        className="btn btn-outline-danger btn-sm" 
                        onClick={() => handleRemoveOcorrencia(index)}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="mb-4">
              <label className="form-label fw-bold">Nova Ocorrência (opcional)</label>
              <div className="input-group">
                <input
                  type="text"
                  className="form-control"
                  value={novaOcorrencia}
                  onChange={(e) => setNovaOcorrencia(e.target.value)}
                  placeholder="Descreva alguma ocorrência durante a banca..."
                  onKeyPress={(e) => e.key === 'Enter' && handleAddOcorrencia(e)}
                />
                <button 
                  className="btn btn-outline-primary" 
                  onClick={handleAddOcorrencia} 
                  disabled={!novaOcorrencia.trim()}
                >
                  Adicionar
                </button>
              </div>
            </div>
          </div>
          <div className="modal-footer d-flex justify-content-between">
            <button className="btn btn-outline-secondary" onClick={handleClose}>
              Cancelar
            </button>
            <div className="d-flex gap-2">
              <button 
                className="btn btn-danger px-4" 
                onClick={handleFinalizar} 
                disabled={counter > 0 || isSubmittingFinalizar || isSubmittingAta || bancaFinalizada}
              >
                {isSubmittingFinalizar ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                    Finalizando...
                  </>
                ) : (
                  <>
                    <i className="bi bi-check-circle me-2"></i>
                    {counter > 0 ? `Aguarde ${counter}s` : bancaFinalizada ? 'Banca Finalizada' : 'Finalizar Banca'}
                  </>
                )}
              </button>
              <button 
                className="btn btn-success px-4" 
                onClick={handleCriarAta} 
                disabled={counter > 0 || isSubmittingAta || isSubmittingFinalizar || !bancaFinalizada}
                title={!bancaFinalizada ? 'A banca precisa estar finalizada para criar a ata' : ''}
              >
                {isSubmittingAta ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                    Criando...
                  </>
                ) : (
                  <>
                    <i className="bi bi-file-earmark-text me-2"></i>
                    {counter > 0 ? `Aguarde ${counter}s` : 'Criar Ata'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AcoesSecretarioModal;